import { Injectable, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { DialogService, panelClassMobileFullscreen } from 'src/app/core/dialog.service';
import { ItemService } from 'src/app/core/item.service';
import { DialogOrderFieldsComponentData } from 'src/app/generic-fields/dialog-order-fields/dialog-order-fields.component';
import { selectCatalog, selectCatalogSettings, selectCatName } from 'src/app/root-store/catalog-store/selectors';
import { SaveReservationResult } from '../cart/cart.model';
import { BackendService } from '../core/backend.service';
import { LocaleService } from '../core/locale/locale.service';
import { ReloadAppointments } from '../root-store/actions';
import { SetTreeItems, SetTreeItemsLoading } from '../root-store/object-tree-store/actions';
import { selectObjectTree, selectObjectTreeErrors, selectSelectedItems } from '../root-store/object-tree-store/selects';
import { RootState } from '../root-store/root-state.model';
import { NavigateTo } from '../root-store/router/actions';
import { selectOrderId } from '../root-store/router/selectors';
import { selectScheduler, selectSchedulerErrors } from '../root-store/scheduler-store/selectors';
import { SetRecurrenceFreq } from '../root-store/time-picker-store/actions';
import { selectTimePicker, selectTimePickerErrors } from '../root-store/time-picker-store/selectors';
import { selectUser } from '../root-store/user-store/selectors';
import { SnackBarService } from '../snackbar.service';
import { AdvancedOptionsForm } from './advanced-options/advanced-options.component';

export interface ItemFieldValues {
  [key: number]: { [key: string]: string }
}

@Injectable({
  providedIn: 'root'
})
export class ObjectChoiceService {
  isSubmitting$ = new BehaviorSubject<boolean>(false)
  allowAddToCart$: Observable<boolean>
  hasErrors$: Observable<boolean>
  hasItemsSelected$ = this.store.select(selectSelectedItems).pipe(map(i => i.length > 0))
  smallScreenStepIndex = signal(1)
  private persistItemsTreeSelection = false
  private persistItemsTreeStorageKey: string | undefined
  public advancedOptions: FormGroup<AdvancedOptionsForm> | undefined
  public isBlock$ = new BehaviorSubject<boolean>(false)
  public showRootIds: number[] | undefined
  navigateEndSubs?: Subscription

  constructor(
    private store: Store<RootState>,
    private translate: TranslateService,
    private localeService: LocaleService,
    private dialog: MatDialog,
    private backend: BackendService,
    private snackBar: MatSnackBar,
    private snackBarSvc: SnackBarService,
    private router: Router,
    private dialogSvc: DialogService,
  ) {
    this.hasErrors$ = combineLatest([
      this.store.select(selectSelectedItems),
      this.store.select(selectObjectTreeErrors),
      this.store.select(selectSchedulerErrors),
      this.store.select(selectTimePickerErrors)
    ]).pipe(
      map(([items, treeErrors, schedulerErrors, timeErrors]) =>
        items.length === 0 || (Object.keys({ ...treeErrors, ...schedulerErrors, ...timeErrors }).length > 0)
      )
    )

    this.allowAddToCart$ = combineLatest([
      this.store.select(selectUser),
      this.store.select(selectSelectedItems),
      this.store.select(selectObjectTreeErrors),
      this.store.select(selectSchedulerErrors),
      this.store.select(selectTimePickerErrors),
      this.isBlock$
    ]).pipe(
      map(([user, items, treeErrors, schedulerErrors, timeErrors, isBlock]) => {
        if (user.isAtLeastAdmin)
          return true

        if (items.length === 0 || treeErrors || timeErrors)
          return false

        if (schedulerErrors && !isBlock) {
          // if all scheduler-errors have couldBypass=true, allow 'add to cart'
          let couldBypassSchedulerErrors = true
          Object.entries(schedulerErrors).forEach(([, errorDetails]) => {
            if (errorDetails['couldBypass'] !== true)
              couldBypassSchedulerErrors = false
          })

          return couldBypassSchedulerErrors
        }

        return true
      })
    )

    // must use observable because ObjectChoiceService is created before the catalog is loaded
    this.store.select(selectCatalogSettings).pipe(filter(s => !!s), take(1)).subscribe(
      settings => this.persistItemsTreeSelection = settings.PersistItemsTreeSelection)

    this.store.select(selectCatName).subscribe(name => this.persistItemsTreeStorageKey = name + '.ItemsTree.SelectedItemIds')
  }

  async saveReservation(itemFieldValues?: Observable<ItemFieldValues>) {
    const isBlock = this.isBlock$.value

    const timePicker = this.store.selectSignal(selectTimePicker)()
    const scheduler = this.store.selectSignal(selectScheduler)()
    const objectTree = this.store.selectSignal(selectObjectTree)()

    // check if booking is allowed.
    // SaveReservation() should be never called if not allowed, because button is disabled if there are errors and user has no permissions
    if ((timePicker.errors || scheduler.errors || objectTree.errors) &&
      !confirm(this.translate.instant('OBJECT_CHOICE.WARN_DIALOG')))
      return

    // optionally get additional Object-OrderFields from the DialogOrderFieldsComponent
    let orderFields$ = itemFieldValues || of({})
    if (!itemFieldValues && !isBlock) {
      const itemsWithOrderFields = objectTree.selectedItems.filter(i => i.item.fields)
      if (itemsWithOrderFields.length) {
        const dialogOrderFieldsComponent = (await import('../generic-fields/dialog-order-fields/dialog-order-fields.component')).DialogOrderFieldsComponent

        orderFields$ = this.dialog.open(dialogOrderFieldsComponent, {
          data: { mode: 'new', items: itemsWithOrderFields } as DialogOrderFieldsComponentData,
          panelClass: panelClassMobileFullscreen
        })
          .afterClosed().pipe(filter(res => !!res))
      }
    }

    // submit values to backend
    orderFields$.pipe(
      tap(() => this.isSubmitting$.next(true)),
      switchMap(orderFields => this.saveReservationToBackend(orderFields)),
      catchError(error => of({ succeeded: false, error, errorDetails: null })),
      tap(() => this.isSubmitting$.next(false)),
    ).subscribe(response => {
      if (response.succeeded) {
        const orderId = this.store.selectSignal(selectOrderId)()

        if (!isBlock || orderId)
          this.store.dispatch(NavigateTo({ route: 'Cart', queryParams: { OrderId: orderId } }))
        else {
          this.advancedOptions?.reset()
          this.store.dispatch(SetRecurrenceFreq({ recurrenceFreq: null }))
          this.store.dispatch(ReloadAppointments({ ignoreSchedulerErrors: true }))
          this.snackBar.open(this.translate.instant('BLOCK_CREATED'), undefined, { duration: 5000 })
        }
      }
      else {
        {
          let errorKey: string | null
          switch (response.error) {
            case 'NoItemPermission':
              errorKey = _('ERROR.NO_PERMISSION')
              break
            case 'InvalidRecurrenceRule':
              errorKey = response.errorDetails?.includes('exceeds max length') ? _('ERROR.TOO_MANY_RECURRENCES') : _('ERROR.INVALID_RECURRENCE_RULE')
              break
            case 'MultipleCatalogs':
              errorKey = _('ERROR.MULTIPLE_CATALOGS')
              break
            case 'TimeNotAvailable':
              errorKey = _('ERROR.INVALID_WISH_TIME')
              break
            default:
              // snackbar for 403 will be shown by WebApiInterceptor, so we don't show it here again
              errorKey = response.error.status === 403 ? null : _('ERROR.UNKNOWN')
              break;
          }

          if (errorKey) {
            if (errorKey === _('ERROR.UNKNOWN'))
              this.snackBarSvc.showUnknownError(response.error)
            else
              this.snackBar.open(this.translate.instant(errorKey, { error: response.error }),
                this.translate.instant('CLOSE'), { duration: 10000 })
          }
        }

        this.store.dispatch(ReloadAppointments()) // just reload appointments, to ensure current appointments are shown
      }
    })
  }

  private saveReservationToBackend(orderFields: { [key: number]: { [key: string]: string } }): Observable<SaveReservationResult> {
    const timePicker = this.store.selectSignal(selectTimePicker)()
    const objectTree = this.store.selectSignal(selectObjectTree)()

    if (!timePicker.startDate || !timePicker.endDate)
      return of({ succeeded: false, error: 'StartEndMissing' })

    const params = {
      itemIds: objectTree.selectedItems.map(i => i.itemId).join(','),
      start: timePicker.startDate.toISOString(),
      end: timePicker.endDate.toISOString()
    } as { [param: string]: string | boolean | number }

    if (timePicker.availabilityPlanId)
      params['availabilityPlanId'] = timePicker.availabilityPlanId

    if (timePicker.quantity && timePicker.quantity > 1)
      params['quantity'] = timePicker.quantity.toString()

    if (this.advancedOptions?.controls.comment.value)
      params['comment'] = this.advancedOptions.controls.comment.value

    if (this.advancedOptions?.controls.externalComment.value)
      params['externalComment'] = this.advancedOptions.controls.externalComment.value

    if (timePicker.recurrenceRule)
      params['recurrence'] = timePicker.recurrenceRule

    const isBlock = this.isBlock$.value
    if (isBlock) {
      params['doubleReservationAllowed'] = this.advancedOptions?.controls.doubleReservationAllowed.value ?? false

      if (params['doubleReservationAllowed'] && this.advancedOptions?.controls.mailOverlappingBlock.value)
        params['mailRecipientOnOverlappingWithBlockInfo'] = this.advancedOptions.controls.mailOverlappingBlock.value
    }

    return isBlock ? this.backend.addBlock(orderFields, params) : this.backend.addToCart(orderFields, params)
  }

  public get PersistentSelectedItemIds(): number[] | null {
    return this.PersistItemsTreeSelectionEnabled ? localStorage.getItem(this.persistItemsTreeStorageKey!)?.split(',').map(Number) ?? null : null
  }

  public set PersistentSelectedItemIds(ids: number[] | null) {
    if (this.PersistItemsTreeSelectionEnabled)
      localStorage.setItem(this.persistItemsTreeStorageKey!, ids?.join(',') ?? '')
  }

  public get PersistItemsTreeSelectionEnabled(): boolean {
    let userIsAtLeastJanitor!: boolean
    this.store.select(selectUser).subscribe(usr => userIsAtLeastJanitor = !!usr.isAtLeastJanitor)
    return userIsAtLeastJanitor && this.persistItemsTreeSelection
  }

  public loadTreeItems(store: Store<RootState>) {
    store.dispatch(SetTreeItemsLoading())

    return this.backend.getItemTree().subscribe(items => store.dispatch(SetTreeItems({ items })))
  }

  public initItemInfoDialog() {
    // open object-info-dialog on initial load (if needed)
    this.openItemInfo(ItemService.getItemFromUrl(this.router.url)?.id)

    // open object-info-dialog on route-changes (if needed)
    if (!this.navigateEndSubs)
      this.navigateEndSubs = this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(async val => await this.openItemInfo(ItemService.getItemFromUrl((val as NavigationEnd).url)?.id))
  }

  // open object-info-dialog
  private async openItemInfo(itemId: number | undefined) {
    if (!itemId)
      return

    const catalog = this.store.selectSignal(selectCatalog)();

    (await this.dialogSvc.openObjectInfo(itemId, true))
      .afterClosed()
      .subscribe(closeResult => {
        if (!closeResult?.redirectedToNewSeoName)
          this.router.navigate(['/' + catalog.name, this.localeService.currentLang()])
      })
  }
}
